import styled from '@emotion/styled';
import { Tooltip, TooltipProps } from '@mui/material';
import { FC } from 'react';
import React from 'react';
import { theme } from '../../theme';

export type ColorName = 'Red' | 'Green' | 'Blue' | 'Yellow';
export type Theme = 'light' | 'dark';
export type Palette = Record<ColorName, string>;
export type Palettes = Record<Theme, Palette>;

export const palettes: Palettes = {
  light: {
    Red: '#FBD5B5',
    Green: '#D6E3BC',
    Blue: '#B6DDE8',
    Yellow: '#F7E1A9',
  },
  dark: {
    Red: '#D18D70',
    Green: '#B4BF86',
    Blue: '#9EBADA',
    Yellow: '#F2DC9C',
  },
};

export const getHexCodeFromName = (name: ColorName, paletteType: Theme = 'light') => {
  const palette = palettes[paletteType];
  const color = palette[name];

  if (color) {
    return color;
  }
  return '';
};

const SyledPalette = styled.div`
  display: grid;
  gap: 3px;
  width: auto;
  grid-template-columns: repeat(2, auto);
  justify-content: left;
  cursor: pointer;
`;

export const ColorPalette: FC<{
  mode: 'copy-to-clipboard' | 'set-color';
  onColorChange?: (color: ColorName) => void;
  theme?: Theme;
}> = ({ mode, onColorChange, theme = 'light' }) => {
  const colorPalette = palettes[theme];
  return (
    <div>
      <SyledPalette>
        {Object.keys(colorPalette).map((color) => (
          <PaletteColor key={color} color={color as ColorName} theme={theme} tooltipPlacement="top" mode={mode} onColorChange={onColorChange} />
        ))}
      </SyledPalette>
    </div>
  );
};

const SyledColor = styled.button<{ hex: string }>`
  background-color: ${(props) => props.hex};
  color: ${(props) => theme.palette.getContrastText(props.hex)};
  margin: 0;
  padding: 0;
  width: 80px;
  height: 80px;
  line-height: 1;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
`;

const defaultTooltipText = 'Klikk for å kopiere';

const PaletteColor: FC<{
  color: ColorName;
  theme: Theme;
  tooltipPlacement?: TooltipProps['placement'];
  mode: 'copy-to-clipboard' | 'set-color';
  onColorChange?: (color: ColorName) => void;
}> = ({ color, theme, tooltipPlacement = 'right', mode, onColorChange }) => {
  const initialTooltipText = mode === 'copy-to-clipboard' ? defaultTooltipText : '';
  const [tooltipText, setTooltipText] = React.useState<string>(initialTooltipText);
  const copyToClipboard = () => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(getHexCodeFromName(color, theme))
        .then(() => {
          setTooltipText('Kopiert!');
        })
        .catch(function (err) {
          throw err !== undefined ? err : new DOMException('The request is not allowed', 'NotAllowedError');
        });
    }
  };
  const handleClick = () => {
    if (mode === 'copy-to-clipboard') {
      copyToClipboard();
    } else {
      onColorChange?.(color);
    }
  };
  const hex = getHexCodeFromName(color, theme);
  return (
    <Tooltip
      title={tooltipText}
      placement={tooltipPlacement}
      onMouseLeave={() => {
        setTimeout(() => setTooltipText(initialTooltipText), 150);
      }}>
      <SyledColor hex={hex} onClick={handleClick}>
        {color}
      </SyledColor>
    </Tooltip>
  );
};
